<script>
import { mapState } from "vuex";
import { get, keys } from "lodash";
import { ValidationProvider, ValidationObserver } from "vee-validate";

export default {
  name: "redefine-password-form",
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  data: () => ({
    password: null,
    passwordConfirmation: null,
    passwordStrength: [],
    strength: null,
    showPassword: false,
    redefined: false,
  }),
  computed: {
    ...mapState("company", ["company"]),
    logo() {
      return get(this.$store, "state.company.company.logo_url", null);
    },
    title() {
      return this.redefined ? "Senha redefinida" : "Defina uma nova senha";
    },
    subtitle() {
      return this.redefined
        ? "Sua senha foi redefinida com sucesso, clique no botão para fazer login."
        : "Ela deve ser diferente da senha anterior";
    },
    isAnonimous() {
      return (
        get(this.$router, "history.current.params.company_label", null) === "anonima"
      );
    },
    passwordsAreEqual() {
      return (
        this.password !== null &&
        this.passwordConfirmation !== null &&
        this.password === this.passwordConfirmation
      );
    },
  },
  methods: {
    shouldShowCompanyLogo() {
      return keys(this.company).length > 0;
    },
    async handleRedefine() {
      try {
        if (this.password == this.passwordConfirmation) {
          this.$vs.loading({
            text: "Salvando...",
          });

          const payload = {
            password: this.password,
            password_confirmation: this.passwordConfirmation,
          };

          const code = get(this.$router, "history.current.params.code", null);

          if (!code) return;
          
          const response = await this.$http.post(
            `api/v1/reset/password/${code}`,
            payload
          );

          if (response.status == 200) {
            this.$vs.loading.close();

            this.redefined = true;

            this.$vs.notify({
              title: "Parabéns!",
              text: "Sua senha foi alterada com sucesso",
              iconPack: "feather",
              icon: "icon-alert-circle",
              color: "success",
            });
          }

          if (response.status == 400) {
            this.$vs.loading.close();
          }
        } else {
          this.$vs.notify({
            title: "Verifique as senhas",
            text: "As senhas informadas não são iguais",
            iconPack: "feather",
            icon: "icon-alert-circle",
            color: "warning",
          });
        }
      } catch (error) {
        this.$vs.loading.close();
        this.$vs.notify({
          title: "Falha ao resetar a senha",
          text:
            error.message ||
            "Falha ao realizar o procedimento contate a nossa equipe de suporte",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "danger",
        });
      }
    },
  },
  watch: {
    password: function (val) {
      function checkPasswordStrength(password) {
        const minLength = 8;
        let passwordStrengthStages = [];

        function hasMinLength() {
          return password.length >= minLength;
        }

        function hasCharsInSequence() {
          const regex = /(abcdefghijklmnopqrstuvwxyz)|(1234567890)|(qwertyuiop)|(asdfghjkl)|(zxcvbnm)/;
          return regex.test(password);
        }

        function hasLowerCase() {
          return /[a-z]/.test(password);
        }

        function hasOnlyLowerCase() {
          const regex = /^[a-z]+$/;
          return regex.test(password);
        }

        function hasUpperCase() {
          return /[A-Z]/.test(password);
        }

        function hasNumber() {
          return /[0-9]/.test(password);
        }

        function hasSpecialChars() {
          return /[!@#$%^&*(),.?":{}|<>]+/.test(password);
        }

        if (password.length < minLength) {
          return passwordStrengthStages;
        }

        if (hasMinLength() || hasCharsInSequence() || hasOnlyLowerCase()) {
          passwordStrengthStages = [...passwordStrengthStages, "Weak"];
        }

        if (
          hasMinLength() &&
          hasLowerCase() &&
          hasUpperCase() &&
          !hasCharsInSequence() &&
          !hasNumber() &&
          !hasSpecialChars() &&
          !hasOnlyLowerCase()
        ) {
          if (!passwordStrengthStages.includes("Weak")) {
            passwordStrengthStages = [...passwordStrengthStages, "Weak"];
          }

          passwordStrengthStages = [...passwordStrengthStages, "Moderate"];
        }

        if (
          hasMinLength() &&
          hasLowerCase() &&
          hasUpperCase() &&
          hasNumber() &&
          !hasSpecialChars() &&
          !hasCharsInSequence()
        ) {
          if (!passwordStrengthStages.includes("Weak")) {
            passwordStrengthStages = [...passwordStrengthStages, "Weak"];
          }

          if (!passwordStrengthStages.includes("Moderate")) {
            passwordStrengthStages = [...passwordStrengthStages, "Moderate"];
          }

          passwordStrengthStages = [...passwordStrengthStages, "Strong"];
        }

        // Check conditions for "Very Strong"
        if (
          hasMinLength() &&
          hasLowerCase() &&
          hasUpperCase() &&
          hasNumber() &&
          hasSpecialChars() &&
          !hasCharsInSequence() &&
          !hasOnlyLowerCase()
        ) {
          if (!passwordStrengthStages.includes("Weak")) {
            passwordStrengthStages = [...passwordStrengthStages, "Weak"];
          }

          if (!passwordStrengthStages.includes("Moderate")) {
            passwordStrengthStages = [...passwordStrengthStages, "Moderate"];
          }

          if (!passwordStrengthStages.includes("Strong")) {
            passwordStrengthStages = [...passwordStrengthStages, "Strong"];
          }

          passwordStrengthStages = [...passwordStrengthStages, "Very Strong"];
        }

        return passwordStrengthStages;
      }

      function getBarColor(strength, index) {
        switch (strength) {
          case "Weak":
            return index === 0 ? "linear-gradient(to right, lightcoral, red)" : "#E0E0E0";
          case "Moderate":
            return index <= 1
              ? "linear-gradient(to right, lightyellow, yellow)"
              : "#E0E0E0";
          case "Strong":
            return index <= 2 ? "linear-gradient(to right, #FED8B1, orange)" : "#E0E0E0";
          case "Very Strong":
            return index <= 3
              ? "linear-gradient(to right, lightgreen, green)"
              : "#E0E0E0";
          default:
            return "#E0E0E0";
        }
      }

      const strengths = checkPasswordStrength(val);
      const bars = document.querySelectorAll(".bar");

      const strengthText = {
        Weak: "Fraca",
        Moderate: "Moderada",
        Strong: "Forte",
        "Very Strong": "Muito forte",
      };

      this.strength = strengthText[strengths[strengths.length - 1]];

      bars.forEach((bar, index) => {
        bar.style.background = getBarColor(strengths[index], index);
      });
    },
  },
  async created() {
    const label = get(this.$route, "params.company_label", null)
    if (label) {
      await this.$store.dispatch("company/fetchCompany", label)
    }
  },
};
</script>

<template>
  <div class="redefine-password-form">
    <img
      v-show="shouldShowCompanyLogo() && !isAnonimous"
      class="redefine-password-form__logo"
      alt="Company Logo"
      :src="logo"
    />
    <h2>{{title}}</h2>
    <p>{{subtitle}}</p>
    <ValidationObserver v-if="!redefined" tag="form" ref="observer" @submit.prevent="handleRedefine()">
      <ValidationProvider
        vid="password"
        name="senha"
        rules="required|min:8"
        v-slot="{ errors }"
      >
        <vs-input
          class="redefine-password-form__input"
          icon="https"
          :type="!showPassword ? 'password' : 'text'"
          v-model="password"
          :style="{ zIndex: 0 }"
          :placeholder="$t('placeholders.password')"
          :label-placeholder="$t('placeholders.password')"
          :danger="Boolean(errors.length)"
          :danger-text="errors[0]"
        />
        <feather-icon
          :class="
            errors.length > 0
              ? 'redefine-password-form__password-eye-error'
              : 'redefine-password-form__password-eye'
          "
          :icon="!showPassword ? 'EyeOffIcon' : 'EyeIcon'"
          @click="() => (showPassword = !showPassword)"
        />
      </ValidationProvider>
      <div class="password-wrapper">
        <p class="password__tips">
          Use uma combinação de letras, números e caracteres especiais. Mínimo de 8
          caracteres.
        </p>
        <div class="password-strength-meter">
          <div class="bar"></div>
          <div class="bar"></div>
          <div class="bar"></div>
          <div class="bar"></div>
        </div>
        <span>{{ strength }}</span>
      </div>
      <ValidationProvider
        name="confirmar_senha"
        rules="required|confirmed:password"
        v-slot="{ errors }"
        style="
           {
            margin-top: 1rem;
          }
        "
      >
        <vs-input
          class="redefine-password-form__input"
          icon="https"
          :type="!showPassword ? 'password' : 'text'"
          v-model="passwordConfirmation"
          :style="{ zIndex: 0 }"
          placeholder="Confirmar senha"
          :label-placeholder="$t('placeholders.password')"
          :danger="Boolean(errors.length)"
          :danger-text="errors[0]"
        />
        <feather-icon
          :class="
            errors.length > 0
              ? 'redefine-password-form__password-eye-error'
              : 'redefine-password-form__password-eye'
          "
          :icon="!showPassword ? 'EyeOffIcon' : 'EyeIcon'"
          @click="() => (showPassword = !showPassword)"
        />
      </ValidationProvider>
      <button
        class="submit-btn bg-primary"
        type="submit"
        :disabled="!passwordsAreEqual"
        :style="{ opacity: passwordsAreEqual ? 1 : 0.5 }"
      >
        Redefinir senha
      </button>
    </ValidationObserver>
    <vs-button
      v-else
      class="submit-btn bg-primary"
      @click="() => $router.push({ name: 'signin' })"
    >
    Fazer login
    </vs-button>
  </div>
</template>

<style lang="scss" scoped>
.submit-btn {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0.85rem 2.5rem;
  color: white;
  font-size: 16px;
  font-weight: 200;
  border: 0px;
  border-radius: 5px;

  margin: 20px 0 20px 0;
  transition: color 0.5s;

  &:hover {
    color: #fcfcfc;
    cursor: pointer;
  }
}
.password-wrapper {
  margin-top: -1rem;
  margin-bottom: 2rem;

  & > span {
    color: #7a869a;
    font-family: Roboto;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    letter-spacing: 0.048px;
  }
}

.password-strength-meter {
  display: flex;
  flex-direction: row;
  margin: 10px 0;

  .bar {
    height: 4px;
    flex-grow: 1;
    margin: 0 5px;
    background-color: #e0e0e0;
  }
}
.redefine-password-form {
  width: 360px;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 1rem;
  gap: 1rem;

  h2 {
    align-self: stretch;
    color: #42526e;
    font-family: Roboto;
    font-size: 26px;
    font-style: normal;
    font-weight: 700;
    line-height: 120%;
    letter-spacing: 0.52px;
  }

  p {
    color: #42526e;
    font-family: Roboto;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
    letter-spacing: 0.4px;
  }

  &__logo {
    max-width: 130px;
    max-height: 150px;
    margin: 0 auto;
    margin-bottom: 05px;
    margin-top: 10px;
    object-fit: contain;
  }

  &__input {
    width: 100%;
  }

  &__password-eye {
    position: absolute;
    top: -30px;
    right: -92%;
    transition-delay: 0s;
    svg {
      cursor: pointer;
      width: 18px;
      color: gray !important;
    }
  }

  &__password-eye-error {
    position: absolute;
    top: -48px;
    right: -90%;
    z-index: 100;
    transition-delay: 0s;
    svg {
      cursor: pointer;
      width: 18px;
      color: gray !important;
    }
  }
}

p.password__tips {
  color: #7a869a;
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 110%;
  letter-spacing: 0.28px;
  margin-bottom: 1rem;
}
</style>
